/* eslint-disable max-lines -- disabled */
/* oxlint-disable eslint-plugin-react-hooks(exhaustive-deps) -- duplicated by eslint and eslint seems better with this rule */
import { A, F, G } from '@mobily/ts-belt';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Warning from '@mui/icons-material/Warning';
import {
  Box,
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports -- no permissions
  Button,
  Collapse,
  FormHelperText,
  FormHelperTextProps,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRow,
  GridRowParams,
  GridRowProps,
} from '@mui/x-data-grid';
import { differenceInYears } from 'date-fns';
import { debounce, keyBy, pick } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  minValue,
  required,
  useDataProvider,
  useNotify,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import {
  Control,
  Controller,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { match } from 'ts-pattern';

import {
  extractPlanLabelForAllocationChoice,
  generateAllocationChoiceId,
} from '@Common/bo/b2b_campaigns';
import { recalculateAmounts } from '@Common/bo/recalculate_campaign_amounts';
import { EMPLOYEE_MAX_DEDUCTIBLE_AGE } from '@Common/constants/b2b/employees';
import { EMPLOYEE_SAVINGS_MAX_COMPANY_VP_WARNING } from '@Common/constants/employee_savings';
import { B2bSavingPlanDeductibility, B2bSavingPlanType } from '@Common/types/b2b/plans';
import {
  B2bCampaignType,
  CampaignAllocationChoiceType,
  GetB2bCampaignAllocationsEndpoint,
  GetB2bCampaignOneResponse,
  PostCalculateMatchingContributionsEndpoint,
} from '@Common/types/bo/b2b/campaigns';
import { B2bCampaignActionHandler } from '@Common/types/bo/b2b/contracts';
import { GetB2bEmployeesEndpoint } from '@Common/types/bo/b2b/employees';
import { GetB2bCompaniesPlansEndpoint } from '@Common/types/bo/b2b/plans';
import { GetInstrumentsResponseItem } from '@Common/types/bo/instruments';
import { MAX_RANGE_ITEMS } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { EmployeeSavingsRiskProfile } from '@Common/types/profile/employee_savings';

import { B2bCompanyPlanSelectInput } from '@sh4res/bo-common/components/B2bCampaigns/B2bCompanyPlanSelectInput';
import { B2bMatchingContributionInput } from '@sh4res/bo-common/components/B2bCampaigns/B2bMatchingContributionAmountInput';
import { B2bPlacementSupportSelectInput } from '@sh4res/bo-common/components/B2bCampaigns/B2bPlacementSupportSelectInput';
import { BlockedCampaignEmployeesSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/BlockedCampaignEmployeesSection';
import { InvalidCampaignEmployeesSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/InvalidCampaignEmployeesSection';
import { MatchingContributionCeilingSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/MatchingContributionCeilingSection';
import { NameMismatchEmployeeSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/NameMismatchEmployeeSection';
import { WarningCampaignEmployeesSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/WarningCampaignEmployeesSection';
import {
  MATCHING_CONTRIBUTION_CEILING_OVERRUNS_FIELD,
  MatchingContributionCeilingOverrunRow,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/matchingContributionCeilings';
import { keepPreferredEmployeesByIdentifier } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/pickPreferredEmployeeByIdentifier';
import {
  extractPlanId,
  handleCampaignTransform,
} from '@sh4res/bo-common/components/B2bCampaigns/transform';
import { AmountInput, CurrencyField } from '@sh4res/bo-common/fields';
import { useCreatePromise, useResourceBasePath } from '@sh4res/bo-common/hooks';
import { inputDisabledProps } from '@sh4res/bo-common/utils';

const testId = 'BoExternalB2bCampaignAllocationsConfirmationStep';

const rowHeight = 74;
const inputsFormHelperTextProps: FormHelperTextProps = {
  sx: { position: 'absolute', bottom: '-16px', left: '2px', margin: 0, whiteSpace: 'nowrap' },
};
const noSpinnerSx = {
  '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]': { MozAppearance: 'textfield' },
} as const;

const handleArrowKeyNavigation = (event: React.KeyboardEvent<HTMLDivElement>): void => {
  if (
    event.target instanceof HTMLInputElement &&
    (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
  ) {
    event.stopPropagation();
  }
};

export type CompanyCollectedChoice = {
  id?: string;
  allocation_type?: CampaignAllocationChoiceType | null;
  plan_id?: number | 'payout' | 'ccb' | null;
  plan_type?: B2bSavingPlanType | null;
  plan_label?: string | null;
  risk_profile?: EmployeeSavingsRiskProfile | null;
  instrument_id?: number | null;
  instrument_name?: string | null;
  deductibility?: B2bSavingPlanDeductibility | null;
  amount?: number | null;
  amount_in_days?: number | null;
  matching_contribution_amount?: number | null;
};

export type AllocationRecord = Pick<
  GetB2bEmployeesEndpoint['Client']['ResponseBody'][number],
  | 'nir'
  | 'ntt'
  | 'firstname'
  | 'lastname'
  | 'social_status'
  | 'pei_investor_profile'
  | 'investor_profile'
  | 'tax_residence_code'
  | 'contract_ids'
  | 'date_of_birth'
> & {
  identifier: string;
  registration_number?: string | null;
  employee_id: number;
  amount: number | null;
  cet_days?: number | null;
  rtt_days?: number | null;
  overtime_days?: number | null;
  paid_leave_days?: number | null;
  seniority_leave_days?: number | null;
  untaken_rest_days?: number | null;
  employee_exists?: boolean;
  employee_fully_onboarded?: boolean;
  is_campaigns_blocked?: boolean;
  is_campaigns_blocked_regulatory?: boolean;
  company_collected_choices?: CompanyCollectedChoice[];
  is_user_closed_or_archived?: boolean;
  /** Set on an ongoing-campaign edit: the employee already answered the consultation. */
  has_made_choice?: boolean;
};

/**
 * Voluntary-deposit campaigns have no net/gross distinction for the matching contribution,
 * so they always report the gross amount.
 */
function pickMatchingContributionAmount(
  choice: {
    matching_contribution_amount?: number | null;
    matching_contribution_gross_amount?: number | null;
  },
  amountType: 'net' | 'gross',
  campaignType: B2bCampaignType,
): number | null | undefined {
  return amountType === 'net' && campaignType !== 'voluntary_deposit'
    ? choice.matching_contribution_amount
    : choice.matching_contribution_gross_amount;
}

export function isAllocationAccepted({
  campaignType,
  amount,
  companyCollectedChoices,
}: {
  campaignType: B2bCampaignType;
  amount: number;
  companyCollectedChoices?: AllocationRecord['company_collected_choices'];
}): boolean {
  return (
    amount > 0 ||
    (campaignType === 'matching_contribution' &&
      G.isNotNullable(companyCollectedChoices) &&
      companyCollectedChoices.length > 0)
  );
}

export function getInvalidEmployees(allocations: AllocationRecord[]) {
  return allocations
    .filter(
      (alloc) =>
        alloc.employee_exists === false ||
        alloc.employee_fully_onboarded === false ||
        alloc.is_user_closed_or_archived === true,
    )
    .map((alloc) => {
      let error = 'employee_not_exists';

      if (alloc.is_user_closed_or_archived) {
        error = 'user_closed';
      } else if (alloc.employee_exists) {
        error = 'employee_not_fully_onboarded';
      }
      return {
        identifier: alloc.identifier,
        firstname: alloc.firstname,
        lastname: alloc.lastname,
        error,
      };
    });
}

export function getWarningEmployees(
  allocations: AllocationRecord[],
  campaignType: B2bCampaignType,
) {
  return allocations
    .filter(
      (alloc) =>
        campaignType === 'voluntary_deposit' &&
        (alloc.amount ?? 0) > EMPLOYEE_SAVINGS_MAX_COMPANY_VP_WARNING,
    )
    .map((alloc) => ({
      identifier: alloc.identifier,
      firstname: alloc.firstname,
      lastname: alloc.lastname,
      warning: 'company_vp_too_large',
    }));
}

export function getAlreadyAnsweredEmployees(allocations: AllocationRecord[]) {
  return allocations
    .filter((alloc) => alloc.has_made_choice ?? false)
    .map((alloc) => ({
      identifier: alloc.identifier,
      firstname: alloc.firstname,
      lastname: alloc.lastname,
      warning: 'already_answered',
    }));
}

export type NameMismatch = { fileFullName: string; dbFullName: string };

type NameMismatchEntry = {
  nir?: string | null;
  ntt?: string | null;
  firstname?: string | null;
  lastname?: string | null;
};

export const getNameMismatches = (
  allocations: NameMismatchEntry[],
  employeeByIdentifier: Record<
    string,
    { firstname?: string | null; lastname?: string | null } | undefined
  >,
) =>
  A.filterMap(allocations, (alloc) => {
    const identifier = alloc.nir ?? alloc.ntt;
    if (
      (G.isNullable(alloc.firstname) && G.isNullable(alloc.lastname)) ||
      G.isNullable(identifier)
    ) {
      return undefined;
    }
    const employee = employeeByIdentifier[identifier];
    if (G.isNullable(employee)) {
      return undefined;
    }
    const namesMatch =
      (G.isNullable(alloc.firstname) ||
        alloc.firstname.toLocaleLowerCase() ===
          F.defaultTo(employee.firstname, '').toLocaleLowerCase()) &&
      (G.isNullable(alloc.lastname) ||
        alloc.lastname.toLocaleLowerCase() ===
          F.defaultTo(employee.lastname, '').toLocaleLowerCase());

    if (namesMatch) {
      return undefined;
    }

    return {
      fileFullName: `${alloc.firstname ?? ''} ${alloc.lastname ?? ''}`.trim(),
      dbFullName: `${employee.firstname ?? ''} ${employee.lastname ?? ''}`.trim(),
    };
  }) as NameMismatch[];

export function getBlockedCampaignEmployees(allocations: AllocationRecord[]) {
  const regulatory: Pick<AllocationRecord, 'firstname' | 'lastname' | 'identifier'>[] = [];
  const restriction: Pick<AllocationRecord, 'firstname' | 'lastname' | 'identifier'>[] = [];

  for (const alloc of allocations) {
    if (alloc.is_campaigns_blocked !== true) {
      continue;
    }

    const employee = {
      identifier: alloc.identifier,
      firstname: alloc.firstname,
      lastname: alloc.lastname,
    };

    if (alloc.is_campaigns_blocked_regulatory === true) {
      regulatory.push(employee);
    } else {
      restriction.push(employee);
    }
  }

  return { regulatory, restriction };
}

function forcePayoutOnlyCompanyCollectedChoices({
  getValues,
  setValue,
  choicesArrayId,
  rowId,
  matchingContributionComputedBy,
}: {
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  choicesArrayId: string;
  rowId: number;
  matchingContributionComputedBy: string | undefined;
}) {
  const currentChoices = (getValues(choicesArrayId) || []) as NonNullable<
    AllocationRecord['company_collected_choices']
  >;

  const totalFromChoices = currentChoices.reduce((sum, choice) => sum + (choice.amount ?? 0), 0);
  const rowAmount = getValues(`allocations.${rowId}.amount`) as number | null | undefined;
  // Row-level amount is the per-employee total from the allocations step; prefer it when set.
  let totalAmount = 0;
  if (G.isNotNullable(rowAmount) && rowAmount > 0) {
    totalAmount = rowAmount;
  } else if (totalFromChoices > 0) {
    totalAmount = totalFromChoices;
  }

  const firstChoice = currentChoices.at(0);
  // Return early once the structure is already correct — do NOT check amount here,
  // because the BO may still be typing it and we must not overwrite their input.
  const isForcedPayout =
    currentChoices.length === 1 &&
    firstChoice?.allocation_type === 'payout' &&
    firstChoice.plan_id === 'payout' &&
    firstChoice.id === 'payout';

  if (isForcedPayout) {
    return;
  }

  const choiceAmount = totalAmount > 0 ? totalAmount : (firstChoice?.amount ?? null);
  setValue(
    choicesArrayId,
    [
      {
        id: 'payout',
        plan_id: 'payout',
        allocation_type: 'payout',
        plan_type: null,
        plan_label: '',
        instrument_id: null,
        deductibility: null,
        amount: choiceAmount,
        matching_contribution_amount: matchingContributionComputedBy === 'company' ? 0 : null,
      },
    ],
    { shouldDirty: true },
  );
  if (totalAmount > 0) {
    setValue(`allocations.${rowId}.amount`, totalAmount, { shouldDirty: true });
  }
  if (matchingContributionComputedBy === 'company') {
    setValue(`allocations.${rowId}.matching_contribution_amount`, 0, { shouldDirty: true });
  }
}

const DetailPanelContent = ({
  row,
  editionDisabled,
}: Pick<GridRowParams, 'row'> & { editionDisabled: boolean }) => {
  const isPayoutOnly = row.is_campaigns_blocked === true;
  const effectiveEditionDisabled = editionDisabled || isPayoutOnly;
  const { setValue, control, getValues } = useFormContext();
  const notify = useNotify();

  const choicesArrayId = `allocations.${row.id}.company_collected_choices`;

  const watchedChoices = useWatch({ name: choicesArrayId });
  const choices = useMemo(
    () => (watchedChoices ?? []) as NonNullable<AllocationRecord['company_collected_choices']>,
    [watchedChoices],
  );

  const matchingContributionComputedBy = getValues('matching_contribution_computed_by');
  const campaignType = getValues('type');
  const resourceBasePath = useResourceBasePath();

  // On a voluntary_deposit campaign, employees aged 70+ can no longer be allocated deductible plans
  const excludeDeductiblePlans =
    G.isNotNullable(row.date_of_birth) &&
    differenceInYears(new Date(), new Date(row.date_of_birth)) >= EMPLOYEE_MAX_DEDUCTIBLE_AGE;

  const [mcMaxAmounts, setMcMaxAmounts] = useState<Record<string, number | null>>({});

  // unique key used to trigger a refetch of the MC remaining amounts
  const mcFetchKey = useMemo(
    () =>
      JSON.stringify(
        choices.map((choice) => ({
          allocation_type: choice.allocation_type,
          plan_id: choice.plan_id,
          instrument_id: choice.instrument_id,
          amount: choice.amount,
          amount_in_days: choice.amount_in_days,
        })),
      ),
    [choices],
  );

  const [fetchCalculateMc] = useCreatePromise<
    PostCalculateMatchingContributionsEndpoint['Client']['RequestBody'],
    PostCalculateMatchingContributionsEndpoint['Client']['ResponseBody']
  >();

  const fetchRequestIdRef = useRef(0);

  const fetchMcRemainingAmounts = useCallback(async () => {
    if (matchingContributionComputedBy === 'distributor') {
      return;
    }
    const formValues = getValues();
    const {
      amount_type_input: amountType,
      reason,
      fiscal_period_start: fiscalPeriodStart,
      reference_year: referenceYear,
      matching_contribution_related_cash_source,
      allocations,
      type,
    } = formValues;

    const employeeAllocation: AllocationRecord | undefined = allocations.find(
      (alloc: AllocationRecord) => alloc.employee_id === row.employee_id,
    );

    if (
      G.isNullable(employeeAllocation?.company_collected_choices) ||
      employeeAllocation.company_collected_choices.length === 0 ||
      (employeeAllocation.company_collected_choices.length === 1 &&
        (G.isNullable(employeeAllocation.company_collected_choices[0].amount) ||
          employeeAllocation.company_collected_choices[0].amount === 0) &&
        type !== 'matching_contribution')
    ) {
      return;
    }

    const requestId = ++fetchRequestIdRef.current;

    try {
      const data = await handleCampaignTransform({
        isDraft: true,
        action: 'create',
        includeEmptyMatchingContributionChoices: true,
      })(formValues);
      const response = await fetchCalculateMc(
        `${resourceBasePath}campaigns/calculate_matching_contributions`,
        {
          data: {
            ...pick(data, 'company_id', 'b2b_contract_ids', 'type'),
            allocations:
              data.allocations as PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['allocations'],
            amount_type_input: amountType,
            matching_contribution_computed_by: matchingContributionComputedBy,
            matching_contribution_related_cash_source,
            reason,
            fiscal_period_start: fiscalPeriodStart,
            reference_year: referenceYear,
          } satisfies PostCalculateMatchingContributionsEndpoint['Client']['RequestBody'],
        },
      );

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }

      const employeeIdStr = row.employee_id.toString();
      const employeeMcData = response.matching_contributions[employeeIdStr];
      if (G.isNotNullable(employeeMcData) && employeeMcData.choices.length > 0) {
        const newMcMaxAmounts: Record<string, number | null> = {};
        const currentChoices = getValues(choicesArrayId) as NonNullable<
          AllocationRecord['company_collected_choices']
        >;

        // handleCampaignTransform reassigns sequential temp IDs to choices,
        // so we build a reverse mapping from temp IDs back to original choice indices
        const transformedEmpAlloc = (
          data.allocations as {
            employee_id: number;
            company_collected_choices?: { id?: string }[];
          }[]
        ).find((a) => a.employee_id === row.employee_id);
        const transformedChoices = transformedEmpAlloc?.company_collected_choices ?? [];

        // The transform filters out choices without allocation_type and preserves order,
        // so we match by position among valid choices
        const tempIdToIdx = new Map<string, number>();
        let tIdx = 0;
        currentChoices.forEach((choice, originalIdx) => {
          if (choice.allocation_type && tIdx < transformedChoices.length) {
            const tempId = transformedChoices[tIdx]?.id;
            if (tempId) {
              tempIdToIdx.set(tempId, originalIdx);
            }
            tIdx++;
          }
        });

        employeeMcData.choices.forEach((responseChoice) => {
          const originalIdx = responseChoice.id ? tempIdToIdx.get(responseChoice.id) : undefined;
          if (originalIdx !== undefined && G.isNotNullable(responseChoice.max_amount)) {
            newMcMaxAmounts[originalIdx] = responseChoice.max_amount;
          }
        });

        setMcMaxAmounts(newMcMaxAmounts);
      }
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      // oxlint-disable-next-line no-console
      // eslint-disable-next-line no-console -- error useful for debugging
      console.error(err);
      notify('resources.bo_common.b2b.campaigns.actions.fetch_mc_remaining_amounts.failure', {
        type: 'error',
      });
    }
  }, [
    getValues,
    choicesArrayId,
    row.employee_id,
    matchingContributionComputedBy,
    fetchCalculateMc,
    resourceBasePath,
    notify,
  ]);

  const debouncedFetchMcRemainingAmounts = useMemo(
    () =>
      debounce(() => {
        void fetchMcRemainingAmounts();
      }, 300),
    [fetchMcRemainingAmounts],
  );

  useEffect(
    () => () => {
      debouncedFetchMcRemainingAmounts.cancel();
    },
    [debouncedFetchMcRemainingAmounts],
  );

  useEffect(() => {
    if (!isPayoutOnly) {
      const currentChoices = (getValues(choicesArrayId) || []) as NonNullable<
        AllocationRecord['company_collected_choices']
      >;
      if (currentChoices.length === 0) {
        setValue(choicesArrayId, [{}]);
      }
      debouncedFetchMcRemainingAmounts();
      return () => {
        debouncedFetchMcRemainingAmounts.cancel();
      };
    }

    forcePayoutOnlyCompanyCollectedChoices({
      getValues,
      setValue,
      choicesArrayId,
      rowId: row.id,
      matchingContributionComputedBy,
    });

    debouncedFetchMcRemainingAmounts();
    return () => {
      debouncedFetchMcRemainingAmounts.cancel();
    };
  }, [
    choicesArrayId,
    isPayoutOnly,
    setValue,
    getValues,
    debouncedFetchMcRemainingAmounts,
    mcFetchKey,
    row.id,
    matchingContributionComputedBy,
  ]);

  const contractIds = getValues('b2b_contract_ids');
  const translate = useTranslate();
  const onAmountChange = useCallback(() => {
    const newChoices: NonNullable<AllocationRecord['company_collected_choices']> =
      getValues(choicesArrayId) ?? [];
    setValue(
      `allocations.${row.id}.amount`,
      newChoices.reduce((acc, choice) => acc + (choice.amount ?? 0), 0),
    );
  }, [getValues, setValue, choicesArrayId, row.id]);

  const onMatchingContributionAmountChange = useCallback(() => {
    const newChoices: NonNullable<AllocationRecord['company_collected_choices']> =
      getValues(choicesArrayId) ?? [];
    setValue(
      `allocations.${row.id}.matching_contribution_amount`,
      newChoices.reduce((acc, choice) => acc + (choice.matching_contribution_amount ?? 0), 0),
    );
  }, [getValues, setValue, choicesArrayId, row.id]);

  const choicesRows = useMemo(
    () =>
      choices.map((_choice, index) => {
        const choiceRowTestId = `${testId}Choice`;
        return (
          <Paper
            key={index}
            elevation={0}
            sx={{
              height: rowHeight,
              display: 'flex',
              alignItems: 'center',
              columnGap: '4px',
              '&:hover .delete-button': {
                visibility: 'visible',
              },
              backgroundColor: index % 2 === 0 ? '#f5f5f5' : 'transparent',
            }}>
            <Box width="50px" />
            <Box flex="4" />
            <Box flex="3" />
            <Box flex="3" />

            <Box flex="4" sx={{ overflowX: 'hidden', pb: '16px', pt: '8px' }}>
              <Controller
                name={`allocations.${row.id}.company_collected_choices.${index}.id`}
                control={control}
                render={({ field }) => (
                  <B2bCompanyPlanSelectInput
                    data-testid={`${choiceRowTestId}_PlanSelectInput`}
                    {...field}
                    allocationId={row.id}
                    contractIds={row.contract_ids}
                    choiceId={index}
                    validate={
                      isPayoutOnly ? [] : [required(translate('ra.validation.required.short'))]
                    }
                    label={false}
                    onKeyDown={handleArrowKeyNavigation}
                    filter={{ b2b_contract_id: contractIds }}
                    restrictedToPayoutOnly={isPayoutOnly}
                    excludeDeductiblePlans={excludeDeductiblePlans}
                    FormHelperTextProps={inputsFormHelperTextProps}
                    {...inputDisabledProps(effectiveEditionDisabled)}
                  />
                )}
              />
            </Box>

            <Box flex="4" sx={{ overflowX: 'hidden', pb: '16px', pt: '8px' }}>
              <B2bPlacementSupportSelectInput
                data-testid={`${choiceRowTestId}_SupportSelectInput`}
                allocationId={row.id}
                control={control}
                choiceId={index}
                label={false}
                validate={isPayoutOnly ? [] : [required(translate('ra.validation.required.short'))]}
                filter={{ b2b_contract_id: contractIds }}
                onKeyDown={handleArrowKeyNavigation}
                FormHelperTextProps={inputsFormHelperTextProps}
                {...inputDisabledProps(effectiveEditionDisabled)}
              />
            </Box>

            {campaignType !== 'matching_contribution' && (
              <Box
                flex="4"
                display="flex"
                alignItems="center"
                sx={{ overflowX: 'auto', pb: '16px', pt: '8px' }}>
                <Controller
                  name={`allocations.${row.id}.company_collected_choices.${index}.amount`}
                  control={control}
                  render={({ field }) => (
                    <AmountInput
                      data-testid={`${choiceRowTestId}_AmountInput`}
                      {...field}
                      source={`allocations.${row.id}.company_collected_choices.${index}.amount`}
                      label={false}
                      validate={
                        isPayoutOnly
                          ? []
                          : [minValue(1), required(translate('ra.validation.required.short'))]
                      }
                      min={1}
                      onKeyDown={handleArrowKeyNavigation}
                      onBlur={onAmountChange}
                      FormHelperTextProps={inputsFormHelperTextProps}
                      {...inputDisabledProps(editionDisabled, noSpinnerSx)}
                    />
                  )}
                />
                <Typography ml={1}>€</Typography>
              </Box>
            )}

            {matchingContributionComputedBy === 'company' && (
              <B2bMatchingContributionInput
                data-testid={`${choiceRowTestId}_McInput`}
                allocationId={row.id}
                choiceIndex={index}
                editionDisabled={false}
                onMatchingContributionAmountChange={onMatchingContributionAmountChange}
                handleArrowKeyNavigation={handleArrowKeyNavigation}
                maxAmount={mcMaxAmounts[index]}
                FormHelperTextProps={inputsFormHelperTextProps}
                inputSx={noSpinnerSx}
              />
            )}

            <Box width="50px" textAlign="center">
              <IconButton
                data-testid={`${testId}_DeleteRowButton`}
                onClick={() => {
                  setValue(choicesArrayId, [
                    ...choices.slice(0, index),
                    ...choices.slice(index + 1),
                  ]);
                  onAmountChange();
                }}
                size="small"
                className="delete-button"
                disabled={effectiveEditionDisabled}
                sx={{ visibility: 'hidden' }}
                aria-label={translate('resources.bo_common.accessibility.delete')}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Paper>
        );
      }),
    [
      choices,
      choicesArrayId,
      onAmountChange,
      editionDisabled,
      effectiveEditionDisabled,
      excludeDeductiblePlans,
      isPayoutOnly,
      control,
      contractIds,
      campaignType,
      row.id,
      row.contract_ids,
      setValue,
      matchingContributionComputedBy,
      onMatchingContributionAmountChange,
      translate,
      mcMaxAmounts,
    ],
  );

  return (
    <Box>
      {choicesRows}
      <Box display="flex" alignItems="center" height={rowHeight}>
        <Button
          data-testid={`${testId}_AddRowButton`}
          startIcon={<AddIcon />}
          sx={{ color: 'grey' }}
          disabled={effectiveEditionDisabled}
          onClick={() => setValue(choicesArrayId, [...choices, {}])}>
          {translate('resources.bo_common.b2b.campaigns.sections.allocations.add_row')}
        </Button>
      </Box>
    </Box>
  );
};

const CustomRow = ({
  row,
  expandedRowIds,
  editionDisabled,
  ...props
}: GridRowProps & { expandedRowIds: number[]; editionDisabled: boolean }) => {
  if (!row) {
    return null;
  }
  const isExpanded = expandedRowIds.includes(row.id);

  return (
    <Box height={props.rowHeight}>
      <GridRow {...props} row={row} rowHeight={rowHeight} />
      {isExpanded && (
        <Collapse in timeout="auto" unmountOnExit>
          <DetailPanelContent row={row} editionDisabled={editionDisabled} />
        </Collapse>
      )}
    </Box>
  );
};

const AmountCell = ({ id }: { id: number | string }) => {
  const translate = useTranslate();
  const amount = useWatch({ name: `allocations.${id}.amount` });
  const choices: AllocationRecord['company_collected_choices'] = useWatch({
    name: `allocations.${id}.company_collected_choices`,
  });

  if (
    G.isNullable(amount) ||
    G.isNullable(choices) ||
    choices.length === 0 ||
    (choices.length === 1 && G.isNullable(choices[0].amount))
  ) {
    return;
  }

  const hasSomeChoiceWithZeroAmount = choices.some(
    (choice: NonNullable<AllocationRecord['company_collected_choices']>[number]) =>
      choice.amount === 0,
  );

  return (
    <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
      <CurrencyField
        data-testid={`${testId}_AllocationAmountField`}
        source="amount"
        currency="eur"
        record={{ amount }}
      />
      {hasSomeChoiceWithZeroAmount && (
        <Tooltip
          title={translate(
            'resources.bo_common.b2b.campaigns.sections.review.errors.warning_zero_amount',
          )}>
          <Warning />
        </Tooltip>
      )}
    </Box>
  );
};

const MatchingContributionAmountCell = ({ id }: { id: number | string }) => {
  const amount = useWatch({ name: `allocations.${id}.matching_contribution_amount` });
  if (!amount) {
    return;
  }
  return (
    <CurrencyField
      data-testid={`${testId}_McAmountField`}
      source="amount"
      currency="eur"
      record={{ amount }}
    />
  );
};

const IdentifierCell = ({ row }: { row: AllocationRecord }) => {
  const translate = useTranslate();
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'center',
        overflowX: 'auto',
      }}>
      {row.identifier}
      {(row.employee_exists === false ||
        row.employee_fully_onboarded === false ||
        row.is_user_closed_or_archived === true) && (
        <FormHelperText error>
          {translate('resources.bo_common.b2b.campaigns.sections.review.errors.employee_not_found')}
        </FormHelperText>
      )}
      {row.is_campaigns_blocked === true && (
        <FormHelperText sx={{ color: 'warning.main' }}>
          {translate(
            row.is_campaigns_blocked_regulatory === true
              ? 'resources.bo_common.b2b.campaigns.sections.review.warnings.campaigns_blocked'
              : 'resources.bo_common.b2b.campaigns.sections.review.warnings.campaigns_blocked_restriction',
          )}
        </FormHelperText>
      )}
    </Box>
  );
};

const extractIdentifiersFromAllocationsFile = (
  allocations: AllocationRecord[] | undefined,
): { nirs: string[]; ntts: string[] } | undefined => {
  if (
    G.isNullable(allocations) ||
    allocations.length === 0 ||
    allocations.length > 500 // After 500 NIRs or NTTs, the request GET /employees will be too big and throw a error 414.
  ) {
    return;
  }

  const nirs: string[] = [];
  const ntts: string[] = [];

  allocations.forEach((allocation) => {
    if (G.isNotNullable(allocation.nir)) {
      nirs.push(allocation.nir);
    } else if (G.isNotNullable(allocation.ntt)) {
      ntts.push(allocation.ntt);
    }
  });

  return {
    nirs,
    ntts,
  };
};

export const useAllocationsColumns = ({
  choiceCollectedBy,
  expandedRowIds,
  toggleRow,
  control,
  editionDisabled,
  amountType,
  campaignType,
  matchingContributionComputedBy,
}: {
  choiceCollectedBy: B2bCampaignActionHandler;
  expandedRowIds: number[];
  toggleRow: (id: number) => void;
  control: Control<FieldValues>;
  editionDisabled: boolean;
  amountType: 'net' | 'gross';
  campaignType: B2bCampaignType;
  matchingContributionComputedBy: B2bCampaignActionHandler;
}) => {
  const translate = useTranslate();

  return useMemo(
    () =>
      [
        choiceCollectedBy === 'company' && {
          field: '_expand',
          headerName: '',
          sortable: false,
          width: 50,
          renderCell: (params: GridRenderCellParams) => (
            <IconButton
              data-testid={`${testId}_ExpandRowButton`}
              size="small"
              onClick={() => toggleRow(params.row.id)}
              aria-label={translate('resources.bo_common.accessibility.toggle')}>
              {expandedRowIds.includes(params.row.id) ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          ),
        },
        {
          field: 'identifier',
          headerName: translate(
            'resources.bo_common.b2b.campaigns.sections.allocations.identifier',
          ),
          flex: 4,
          renderCell: ({ row }: GridRenderCellParams) => <IdentifierCell row={row} />,
        },
        {
          field: 'firstname',
          headerName: translate('resources.bo_common.b2b.employees.fields.firstname'),
          flex: 3,
        },
        {
          field: 'lastname',
          headerName: translate('resources.bo_common.b2b.employees.fields.lastname'),
          flex: 3,
        },
        choiceCollectedBy === 'company' && {
          field: '_plan',
          headerName: translate('resources.bo_common.b2b.campaigns.sections.allocations.plan'),
          flex: 4,
          sortable: false,
        },
        choiceCollectedBy === 'company' && {
          field: '_placement_support',
          headerName: translate(
            'resources.bo_common.b2b.campaigns.sections.allocations.placement_support',
          ),
          flex: 4,
          sortable: false,
        },
        campaignType !== 'matching_contribution' && {
          field: choiceCollectedBy === 'company' ? '_amount' : 'amount',
          headerName: translate(
            `resources.bo_common.b2b.campaigns.sections.review.${amountType === 'gross' ? 'gross_amount' : 'net_amount'}${campaignType === 'voluntary_deposit' ? '_deposit' : ''}`,
          ),
          flex: 4,
          sortable: false,
          renderCell:
            choiceCollectedBy === 'company'
              ? (params: GridRenderCellParams) => <AmountCell id={params.id} />
              : (params: GridRenderCellParams) => (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'auto auto',
                      gridColumnGap: '10px',
                    }}>
                    <Controller
                      name={`allocations.${params.id}.amount`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <AmountInput
                          data-testid={`${testId}_RowAmountInput`}
                          {...field}
                          source={`allocations.${params.id}.amount`}
                          label={false}
                          validate={[minValue(0)]}
                          min={0}
                          onKeyDown={handleArrowKeyNavigation}
                          sx={{
                            '& input': {
                              paddingTop: fieldState.error ? '10px' : undefined,
                            },
                          }}
                          {...inputDisabledProps(editionDisabled)}
                        />
                      )}
                    />
                    <Box margin={'auto'}>€</Box>
                  </Box>
                ),
        },
        matchingContributionComputedBy === 'company' &&
          choiceCollectedBy === 'company' && {
            field: '_matching_contribution_amount',
            headerName: translate(
              'resources.bo_common.b2b.campaigns.sections.allocations.matching_contribution_amount',
            ),
            flex: 4,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
              <MatchingContributionAmountCell id={params.id} />
            ),
          },
        choiceCollectedBy === 'company' && {
          field: '_delete',
          headerName: '',
          width: 50,
          sortable: false,
        },
      ].filter((column) => column !== false),
    [
      campaignType,
      choiceCollectedBy,
      expandedRowIds,
      toggleRow,
      control,
      editionDisabled,
      amountType,
      translate,
      matchingContributionComputedBy,
    ],
  );
};

export const AllocationsConfirmationStep = ({
  action,
  isBoExternal,
  isOngoingConsultationEdit,
  distributorStepsOverride = false,
}: {
  action: 'create' | 'edit';
  isBoExternal: boolean;
  isOngoingConsultationEdit: boolean;
  distributorStepsOverride?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { control, getValues, setValue } = useFormContext();
  const record = useRecordContext<GetB2bCampaignOneResponse>();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const resourceBasePath = useResourceBasePath();

  const [expandedRowIds, setExpandedRowIds] = useState<number[]>([]);
  const [nameMismatches, setNameMismatches] = useState<NameMismatch[]>([]);

  const toggleRow = (id: number) => {
    setExpandedRowIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const { fields } = useFieldArray({
    control,
    name: 'allocations',
  });

  const [
    companyId,
    allocations,
    campaignType,
    contractIds,
    amountType,
    choiceCollectedBy,
    allocationComputedBy,
    matchingContributionComputedBy,
    amountInput,
  ] = useWatch({
    name: [
      'company_id',
      'allocations',
      'type',
      'b2b_contract_ids',
      'amount_type_input',
      'choice_collected_by',
      'allocation_computed_by',
      'matching_contribution_computed_by',
      'amount_input',
      'reason',
      'fiscal_period_start',
    ],
  });

  const editionDisabled = useMemo(() => {
    // An ongoing consultation stays editable: answered employees have their choices re-spread over
    // the new amount, or cancelled outright when they are removed.
    if (isOngoingConsultationEdit) {
      return false;
    }

    if (allocationComputedBy !== 'distributor') {
      return false;
    }
    if (isBoExternal) {
      // Always disabled in external BO when allocations computed by distributor
      return true;
    }
    if (distributorStepsOverride) {
      return false;
    }

    return record?.status === 'campaign_awaiting_customer_confirmation';
  }, [
    record,
    isBoExternal,
    allocationComputedBy,
    isOngoingConsultationEdit,
    distributorStepsOverride,
  ]);

  useEffect(() => {
    const fetchAllRecords = async () => {
      // allocations is set and validated, user has gone back to this step
      if (
        allocations &&
        allocations.every((alloc: AllocationRecord) => G.isNotNullable(alloc.employee_exists))
      ) {
        return;
      }

      setIsLoading(true);
      const allData: AllocationRecord[] = [];

      const extract = extractIdentifiersFromAllocationsFile(allocations);

      const { data: employeesData } = await dataProvider.getList<
        GetB2bEmployeesEndpoint['Client']['ResponseBody'][number]
      >(`${resourceBasePath}employees`, {
        filter: {
          company_id: companyId,
          contract_ids: contractIds,
          nir: extract?.nirs ?? undefined,
        },
        sort: { field: 'created_at', order: 'DESC' },
        pagination: { page: 1, perPage: MAX_RANGE_ITEMS },
      });

      if (G.isNotNullable(extract) && extract.ntts.length > 0) {
        const { data: employeesDataFromNtts } = await dataProvider.getList<
          GetB2bEmployeesEndpoint['Client']['ResponseBody'][number]
        >(`${resourceBasePath}employees`, {
          filter: {
            company_id: companyId,
            contract_ids: contractIds,
            ntt: extract.ntts,
          },
          sort: { field: 'created_at', order: 'DESC' },
          pagination: { page: 1, perPage: MAX_RANGE_ITEMS },
        });

        employeesData.push(...employeesDataFromNtts);
      }

      // When several employees share the same NIR/NTT (For example, one closed and one current),
      // keep only the preferred one so it's the only row shown, whether
      // allocations come from an uploaded file or from listing every employee of the company.
      const dedupedEmployeesData = keepPreferredEmployeesByIdentifier(employeesData);

      let allocationsMap: Record<
        number,
        GetB2bCampaignAllocationsEndpoint['Client']['ResponseBody'][number] | undefined
      > = {};
      if (action === 'edit') {
        const { data: allocationsData } = await dataProvider.getList<
          GetB2bCampaignAllocationsEndpoint['Client']['ResponseBody'][number]
        >(`${resourceBasePath}campaigns/allocations`, {
          filter: { campaign_id: record?.id },
          sort: { field: 'employee_id', order: 'ASC' },
          pagination: { page: 1, perPage: MAX_RANGE_ITEMS },
        });
        allocationsMap = keyBy(allocationsData, 'employee_id');
      }

      const { data: plans } = await dataProvider.getList<
        GetB2bCompaniesPlansEndpoint['Client']['ResponseBody'][number]
      >(`${resourceBasePath}companies/plans`, {
        pagination: { page: 1, perPage: 100 },
        filter: { b2b_contract_id: contractIds },
        sort: { field: 'created_at', order: 'ASC' },
      });

      const instrumentIds = plans.flatMap((plan) =>
        plan.shares_plans.flatMap((sp) => sp.instruments_universe),
      );

      const { data: ccbInstrumentsLinkedToPlans } =
        campaignType === 'participation' && instrumentIds.length > 0
          ? await dataProvider.getList<GetInstrumentsResponseItem>('instruments', {
              filter: {
                id: instrumentIds,
                type: 'ccb',
              },
              sort: { field: 'id', order: 'DESC' },
              pagination: { page: 1, perPage: instrumentIds.length },
            })
          : { data: [] };

      const ccbInstrumentIds = ccbInstrumentsLinkedToPlans.map((instrument) => instrument.id);

      allData.push(
        ...dedupedEmployeesData.map((entry) => ({
          ...pick(
            entry,
            'nir',
            'ntt',
            'firstname',
            'lastname',
            'registration_number',
            'social_status',
            'investor_profile',
            'pei_investor_profile',
            'tax_residence_code',
            'contract_ids',
            'date_of_birth',
            'is_campaigns_blocked',
            'is_campaigns_blocked_regulatory',
            'is_user_closed_or_archived',
          ),
          identifier: (entry.nir ?? entry.ntt) as string,
          employee_id: entry.id,
          amount:
            action === 'edit'
              ? match(amountType)
                  .with('net', () => allocationsMap[entry.id]?.net_amount ?? null)
                  .with('gross', () => allocationsMap[entry.id]?.gross_amount ?? null)
                  .otherwise(() => {
                    throw new Error('Invalid amount type');
                  })
              : 0,
          has_made_choice: allocationsMap[entry.id]?.invested_amount !== 0,
          matching_contribution_amount: allocationsMap[entry.id]?.company_collected_choices.reduce(
            (acc, choice) =>
              acc + (pickMatchingContributionAmount(choice, amountType, campaignType) ?? 0),
            0,
          ),
          employee_exists: true,
          employee_fully_onboarded: entry.approbation_state === 'finished',
          company_collected_choices: allocationsMap[entry.id]?.company_collected_choices.map(
            (choice) => {
              const choiceId = choice.allocation_type === 'payout' ? 'payout' : undefined;
              const planLabel = extractPlanLabelForAllocationChoice({
                plans,
                planId: choice.plan_id,
                planType: choice.plan_type,
              });
              const isChoiceConcernCcb =
                G.isNotNullable(choice.instrument_id) &&
                ccbInstrumentIds.includes(choice.instrument_id);

              return {
                ...choice,
                id: generateAllocationChoiceId({
                  id: choiceId,
                  planType: choice.plan_type,
                  deductibility: choice.deductibility,
                  planId: choice.plan_id,
                  isChoiceConcernCcb,
                }),
                plan_id: extractPlanId(choice, isChoiceConcernCcb),
                amount: amountType === 'net' ? choice.amount : choice.gross_amount,
                plan_type: choice.plan_type,
                plan_label: planLabel ?? '',
                instrument_name: choice.instrument_name,
                matching_contribution_amount:
                  amountType === 'net' && campaignType !== 'voluntary_deposit'
                    ? choice.matching_contribution_amount
                    : choice.matching_contribution_gross_amount,
              };
            },
          ),
        })),
      );
      // allocations set from file but not yet validated
      if (allocations) {
        // allData was already deduplicated, so it filters out duplicate employees.
        const employeeMap: Record<string, AllocationRecord | undefined> = keyBy(
          allData,
          'identifier',
        );
        setNameMismatches(getNameMismatches(allocations, employeeMap));
        setValue(
          'allocations',
          allocations.map((alloc: AllocationRecord) => {
            const identifier = (alloc.nir ?? alloc.ntt) as string;
            const employee = employeeMap[identifier];
            return {
              ...alloc,
              identifier,
              contract_ids: employee?.contract_ids ?? [],
              employee_id: employee?.employee_id,
              employee_exists: employee?.employee_exists ?? false,
              employee_fully_onboarded: employee?.employee_fully_onboarded ?? false,
              is_user_closed_or_archived: employee?.is_user_closed_or_archived ?? false,
              is_campaigns_blocked: employee?.is_campaigns_blocked ?? false,
              is_campaigns_blocked_regulatory: employee?.is_campaigns_blocked_regulatory ?? false,
              social_status: employee?.social_status,
              investor_profile: employee?.investor_profile,
              pei_investor_profile: employee?.pei_investor_profile,
              tax_residence_code: employee?.tax_residence_code,
              date_of_birth: employee?.date_of_birth,
              registration_number: employee?.registration_number,
              company_collected_choices: alloc.company_collected_choices?.map((choice) => {
                const choiceId = choice.allocation_type === 'payout' ? 'payout' : undefined;
                const planLabel = extractPlanLabelForAllocationChoice({
                  plans,
                  planId: choice.plan_id,
                  planType: choice.plan_type,
                });
                const isChoiceConcernCcb =
                  G.isNotNullable(choice.instrument_id) &&
                  ccbInstrumentIds.includes(choice.instrument_id);
                return {
                  ...choice,
                  plan_id: extractPlanId(choice, isChoiceConcernCcb),
                  plan_label: planLabel ?? '',
                  id: generateAllocationChoiceId({
                    id: choiceId,
                    planType: choice.plan_type,
                    deductibility: choice.deductibility,
                    planId: choice.plan_id,
                    isChoiceConcernCcb,
                  }),
                };
              }),
            };
          }),
          { shouldDirty: true },
        );
      } else if (record?.status === 'campaign_awaiting_customer_confirmation') {
        setValue(
          'allocations',
          allData.filter((alloc: AllocationRecord) => (alloc.amount ?? 0) > 0),
          { shouldDirty: true },
        );
      } else {
        setValue('allocations', allData, { shouldDirty: true });
      }

      setIsLoading(false);
    };

    fetchAllRecords();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, contractIds]);

  // A changed operation amount re-spreads every allocation over the new total, keeping each
  // employee's share. The backend then re-spreads their choices by the same ratio.
  useEffect(() => {
    if (!isOngoingConsultationEdit || G.isNullable(amountInput)) {
      return;
    }
    const rows = (allocations ?? []) as AllocationRecord[];
    const recalculated = recalculateAmounts(
      rows.map((row, index) => ({ key: index, amount: row.amount ?? 0 })),
      amountInput,
    );

    recalculated.forEach((entry) => {
      setValue(`allocations.${entry.key}.amount`, entry.nextAmount, { shouldDirty: true });
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [amountInput, isOngoingConsultationEdit]);

  const columns: GridColDef[] = useAllocationsColumns({
    choiceCollectedBy,
    expandedRowIds,
    toggleRow,
    control,
    editionDisabled,
    amountType,
    campaignType,
    matchingContributionComputedBy,
  });

  const invalidEmployees = useMemo(
    () => getInvalidEmployees((allocations ?? []) as AllocationRecord[]),
    [allocations],
  );

  const { regulatory: blockedCampaignEmployees, restriction: restrictionBlockedEmployees } =
    useMemo(
      () => getBlockedCampaignEmployees((allocations ?? []) as AllocationRecord[]),
      [allocations],
    );

  const warningEmployees = useMemo(
    () => [
      ...getWarningEmployees((allocations ?? []) as AllocationRecord[], campaignType),
      ...restrictionBlockedEmployees.map((employee) => ({
        ...employee,
        warning: 'campaigns_blocked_restriction',
      })),
      ...(isOngoingConsultationEdit
        ? getAlreadyAnsweredEmployees((allocations ?? []) as AllocationRecord[])
        : []),
    ],
    [allocations, campaignType, isOngoingConsultationEdit, restrictionBlockedEmployees],
  );

  const ceilingOverruns: MatchingContributionCeilingOverrunRow[] | undefined = useWatch({
    name: MATCHING_CONTRIBUTION_CEILING_OVERRUNS_FIELD,
  });

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Typography>
        {translate(
          `resources.bo_common.b2b.campaigns.sections.confirmation.${amountType === 'gross' ? 'gross_amount' : 'net_amount'}${campaignType === 'voluntary_deposit' ? '_deposit' : ''}`,
        )}
        :{' '}
        <CurrencyField
          data-testid={`${testId}_TotalAmountField`}
          source="amount"
          currency="eur"
          record={{
            amount:
              campaignType === 'voluntary_deposit'
                ? (allocations?.reduce(
                    (total: number, alloc: AllocationRecord) =>
                      total +
                      (alloc.company_collected_choices?.reduce(
                        (subTotal, choice) => subTotal + (choice.amount ?? 0),
                        0,
                      ) ?? 0),
                    0,
                  ) ?? 0)
                : getValues('amount_input'),
          }}
        />
      </Typography>
      <DataGrid
        editMode="cell"
        rows={fields.map((field, index) => ({
          ...field,
          id: index,
        }))}
        columns={columns}
        disableSelectionOnClick
        initialState={{
          pagination: {
            pageSize: 10,
          },
        }}
        components={{ Row: CustomRow }}
        componentsProps={{ row: { expandedRowIds, editionDisabled } }}
        loading={isLoading}
        getRowHeight={(params) => {
          if (!expandedRowIds.includes(params.id as number)) {
            return rowHeight;
          }
          const nChoices =
            getValues(`allocations.${params.id}.company_collected_choices`)?.length ?? 0;
          // number of choices + add row + header row
          return (nChoices + 2) * rowHeight;
        }}
        rowsPerPageOptions={[10, 20, 50]}
        disableColumnMenu
        sx={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiTablePagination-root .MuiToolbar-root': {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      />

      <InvalidCampaignEmployeesSection invalidEmployees={invalidEmployees} />
      <WarningCampaignEmployeesSection warningEmployees={warningEmployees} />
      <BlockedCampaignEmployeesSection blockedCampaignEmployees={blockedCampaignEmployees} />
      <NameMismatchEmployeeSection nameMismatches={nameMismatches} />
      <MatchingContributionCeilingSection ceilingOverruns={ceilingOverruns ?? []} />
    </Box>
  );
};
